import {
	HTTP_STATUS,
	AUTH_LOCAL_STORAGE_KEY,
	useLoginMutation,
	useMeQuery,
} from '@lqa-frontend/api-services';
import type {
	AuthContextValue,
	CoreApiError,
	LoginResult,
} from '@lqa-frontend/api-services';
import type { AxiosError } from 'axios';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { AuthContext } from './auth-context';

export function AuthContextProvider({
	children,
	onLogout,
}: PropsWithChildren<AuthContextProviderProps>) {
	const login = useLoginMutation({
		onSuccess: async (result) => {
			setToken(result.data.accessToken);
		},
	});
	const meQuery = useMeQuery();
	const [, setToken] = useLocalStorage<string | null>(
		AUTH_LOCAL_STORAGE_KEY,
		null
	);

	const value = useMemo<AuthContextValue>(
		() => ({
			login: async (data): Promise<LoginResult> => {
				try {
					const result = await login.mutateAsync({ data });

					if (result.status === HTTP_STATUS.CREATED) {
						await meQuery.refetch();
					}

					return {
						status: result.status === HTTP_STATUS.CREATED ? 'success' : 'error',
						message:
							result.status === HTTP_STATUS.CREATED
								? 'Login successful'
								: 'Invalid credentials',
					};
				} catch (error: unknown) {
					const axiosError = error as AxiosError<CoreApiError>;
					const status = axiosError.response?.status;
					const apiMessage = axiosError.response?.data?.message;
					const message =
						status === HTTP_STATUS.UNAUTHORIZED
							? 'Invalid credentials'
							: typeof apiMessage === 'string'
								? apiMessage
								: (axiosError.message ?? 'Unknown error');

					return {
						status: 'error',
						message,
					};
				}
			},
			logout: async () => {
				localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
				onLogout?.();
			},
			user: meQuery.data || null,
			isUserLoading: meQuery.isFetching || login.status === 'pending',
			isUserLoaded: meQuery.isFetched,
		}),
		[login, meQuery.data, meQuery.isFetched, meQuery.isFetching]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface AuthContextProviderProps {
	onLogout: () => void;
}
